import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarDays, LuHash } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import useBudgetText from '../budgetText';
import { pick } from '../budgetColumns';

const MUTED = 'tw-text-slate-400 dark:tw-text-slate-500';

export const initialsOf = (name) => {
  const words = String(name || '').split(/\s+/).filter((word) => /[\p{L}\p{N}]/u.test(word));
  if (words.length === 0) return '?';
  return `${words[0][0] || ''}${words.length > 1 ? words[1][0] || '' : ''}`.toLocaleUpperCase('tr-TR');
};

export const isIncome = (budget) => budget?.['GiderTipi'] === 'Gelir';
export const isPassive = (budget) => budget?.AktifDurum === 'Pasif';

/** Unvan of the row as the old table showed it (falls back to BütçeKaynak, then an employee's name). */
export const partyName = (budget, getEmployeeName) => {
  const prefix = isIncome(budget) ? 'Musteri' : 'Gonderici';
  return pick(budget, `${prefix}Unvan`, 'BütçeKaynak') || getEmployeeName(budget['BütçeKaynak']) || '';
};

/** Alındı / Ödendi (green), Beklemede (amber), anything else such as İptal (red). */
export const PaymentBadge = ({ value }) => {
  const { statusLabel } = useBudgetText();
  if (!value) return <span className={MUTED}>-</span>;
  let tone = 'danger';
  if (value === 'Alındı' || value === 'Ödendi') tone = 'success';
  else if (value === 'Beklemede') tone = 'warning';
  return <Badge tone={tone}>{statusLabel(value)}</Badge>;
};

PaymentBadge.propTypes = { value: PropTypes.string };

/** Aktif / Pasif of the record (AktifDurum; missing counts as Aktif, as before). */
export const ActiveBadge = ({ passive }) => {
  const { statusLabel } = useBudgetText();
  return (
    <Badge tone={passive ? 'neutral' : 'success'}>
      <span aria-hidden="true" className={`tw-h-1.5 tw-w-1.5 tw-rounded-full ${passive ? 'tw-bg-slate-400' : 'tw-bg-emerald-500'}`} />
      {statusLabel(passive ? 'Pasif' : 'Aktif')}
    </Badge>
  );
};

ActiveBadge.propTypes = { passive: PropTypes.bool };

/** Tooltip text of a cell (the full value where the cell is cut short). */
export const makeCellTitle = (columns, getEmployeeName) => (budget, key) => {
  const kind = columns[key]?.kind;
  if (kind === 'party') return partyName(budget, getEmployeeName) || undefined;
  if (kind === 'status') return undefined;
  if (kind === 'documentDate') return pick(budget, 'BelgeTarihi', 'BütçeTarihi') || undefined;
  if (kind === 'total') return undefined;
  const value = pick(budget, key);
  return value === '' || typeof value === 'object' ? undefined : String(value);
};

/** Content of one income / expense table cell; inactive (Pasif) records are drawn muted. */
const BudgetCell = ({ columnKey, budget, columns, getEmployeeName }) => {
  const { formatAmount, formatInt, formatDate } = useBudgetText();
  const passive = isPassive(budget);
  const textClass = `tw-block tw-truncate ${passive ? MUTED : ''}`;
  const plain = (value) => (value === '' || value === undefined || value === null
    ? <span className={MUTED}>-</span>
    : <span className={textClass}>{String(value)}</span>);
  const number = (text) => (text === '-'
    ? <span className={MUTED}>-</span>
    : <span className={`${textClass} tw-tabular-nums`}>{text}</span>);

  switch (columns[columnKey]?.kind) {
    case 'party': {
      const name = partyName(budget, getEmployeeName);
      const income = isIncome(budget);
      return (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
          <span aria-hidden="true" className={`tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-[10px] tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${passive ? 'tw-from-slate-300 tw-to-slate-500' : income ? 'tw-from-teal-400/90 tw-to-emerald-600/90' : 'tw-from-rose-400/90 tw-to-orange-500/90'}`}>
            {initialsOf(name)}
          </span>
          <span className={`tw-min-w-0 tw-truncate tw-font-semibold ${passive ? 'tw-text-slate-500 dark:tw-text-slate-400' : 'tw-text-slate-900 dark:tw-text-white'}`}>{name || '-'}</span>
        </span>
      );
    }
    case 'code':
      return plain(pick(budget, columnKey));
    case 'date':
      return plain(formatDate(pick(budget, columnKey)));
    case 'documentDate':
      return plain(formatDate(pick(budget, 'BelgeTarihi', 'BütçeTarihi')));
    case 'total': {
      const text = formatAmount(pick(budget, 'Tutar', 'BütçeMiktarı'));
      return text === '-'
        ? <span className={MUTED}>-</span>
        : <span className={`tw-block tw-truncate tw-font-semibold tw-tabular-nums ${passive ? MUTED : 'tw-text-slate-900 dark:tw-text-white'}`}>{text}</span>;
    }
    case 'amount':
      return number(formatAmount(budget[columnKey]));
    case 'int':
      return number(formatInt(budget[columnKey]));
    case 'status':
      return <PaymentBadge value={budget['ÖdemeDurumu']} />;
    default:
      return plain(pick(budget, columnKey));
  }
};

BudgetCell.propTypes = {
  columnKey: PropTypes.string.isRequired,
  budget: PropTypes.object.isRequired,
  columns: PropTypes.object.isRequired,
  getEmployeeName: PropTypes.func.isRequired,
};

/** One row of the card list (narrow screens). */
export const BudgetCardRow = ({ budget, getEmployeeName }) => {
  const { formatAmount, formatDate } = useBudgetText();
  const passive = isPassive(budget);
  const income = isIncome(budget);
  const name = partyName(budget, getEmployeeName);
  const amount = formatAmount(pick(budget, 'Tutar', 'BütçeMiktarı'));
  const date = formatDate(pick(budget, 'BelgeTarihi', 'BütçeTarihi'));
  const gib = pick(budget, 'GibNumarasi');
  return (
    <>
      <span aria-hidden="true" className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${passive ? 'tw-from-slate-300 tw-to-slate-500' : income ? 'tw-from-teal-400 tw-to-emerald-600' : 'tw-from-rose-400 tw-to-orange-500'}`}>
        {initialsOf(name)}
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-2">
          <span className={`tw-min-w-0 tw-truncate tw-text-[15px] tw-font-semibold ${passive ? 'tw-text-slate-500 dark:tw-text-slate-400' : 'tw-text-slate-900 dark:tw-text-white'}`}>{name || '-'}</span>
          <PaymentBadge value={budget['ÖdemeDurumu']} />
        </span>
        <span className="tw-mt-0.5 tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-[13px]">
          <span className={`tw-font-semibold tw-tabular-nums ${passive ? MUTED : 'tw-text-slate-800 dark:tw-text-slate-100'}`}>
            {amount}{amount !== '-' && budget['ParaBirimi'] ? ` ${budget['ParaBirimi']}` : ''}
          </span>
          {passive && <ActiveBadge passive />}
        </span>
        {(date || gib) && (
          <span className="tw-mt-1 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
            {date && (
              <span className="tw-inline-flex tw-items-center tw-gap-1">
                <LuCalendarDays aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                {date}
              </span>
            )}
            {gib && (
              <span className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1">
                <LuHash aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                <span className="tw-truncate">{gib}</span>
              </span>
            )}
          </span>
        )}
      </span>
    </>
  );
};

BudgetCardRow.propTypes = { budget: PropTypes.object.isRequired, getEmployeeName: PropTypes.func.isRequired };

export default BudgetCell;
